import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
} from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AccountService } from '@app/account/services/account.service';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { WebEnvironmentService } from '@app/shared/services/web-environment.service';
import { DfFormFieldBuilder, DfFormFieldConfig } from '@degreed/fresco';
import { FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';

/**
 * Reset Password component is the page you reach after clicking a link in an email to actually reset your password.
 * See forgot-password-form.component if you are looking for the page to input an email to receive a link to reset
 * the password.
 */
@Component({
  selector: 'dgx-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordComponent
  extends SubscriberBaseDirective
  implements AfterViewInit {
  public model: { [key: string]: string | number } = {};
  public options: FormlyFormOptions = {};
  public form: FormGroup = new FormGroup({});
  public fields: DfFormFieldConfig[] = [];
  public isSaving: boolean = false;
  public hasTokenError: boolean = false;
  public passwordChanged: boolean = false;
  public hasPasswordError: boolean = false;
  public i18n = this.translate.instant([
    'Core_FieldRequired',
    'AccountController_ResetPassword',
    'AccountController_Continue',
    'signupForm_RegisterEmailLabel',
    'signupForm_CreatePasswordTooltip',
    'resetPasswordForm_NewPasswordLabel',
    'resetPasswordForm_ConfirmPasswordLabel',
    'resetPasswordForm_InvalidEmail',
    'resetPasswordForm_InvalidPassword',
    'resetPasswordForm_InvalidConfirmPassword',
    'AccountSvc_PasswordResetError',
    'AccountSvc_ChangeLinkFail',
    'AccountSvc_ChangeSuccess',
  ]);

  public ngAfterViewInit() {
    this.fields = this.buildFormFields();
    this.cdr.detectChanges();
  }

  public get code(): string {
    return this.activeRoute.snapshot.queryParams.code;
  }

  public get degreedGlassesLogo(): string {
    return this.webEnvironmentService.getBlobUrl(
      '/content/img/branding/degreed-logo-glasses.svg',
      true
    );
  }

  public get error(): string {
    if (this.hasPasswordError) {
      return this.i18n.AccountSvc_PasswordResetError;
    } else if (this.hasTokenError) {
      return this.i18n.AccountSvc_ChangeLinkFail;
    }
    return undefined;
  }

  public get specialInstructions(): string {
    if (this.passwordChanged) {
      return this.i18n.AccountSvc_ChangeSuccess;
    }
    return undefined;
  }

  private buildFormFields(): DfFormFieldConfig[] {
    return [
      this.builder
        .email()
        .labeled(this.i18n.signupForm_RegisterEmailLabel)
        .validatedBy(Validators.required, Validators.email)
        .withErrorMessages({
          required: this.i18n.resetPasswordForm_InvalidEmail,
          email: this.i18n.resetPasswordForm_InvalidEmail,
        })
        .autofocused()
        .build(),
      this.builder
        .passwordResetGroup(
          (newPassword) =>
            newPassword
              .labeled('resetPasswordForm_NewPasswordLabel')
              // TODO: uncomment when withTip is working in fresco
              // .withTip(this.i18n.signupForm_CreatePasswordTooltip)
              // .withHelp('')
              .withErrorMessages({
                required: this.i18n.resetPasswordForm_InvalidPassword,
              })
              .build(),
          (confirmPassword) =>
            confirmPassword
              .labeled('resetPasswordForm_ConfirmPasswordLabel')
              .withErrorMessages({
                fieldMatch: this.i18n.resetPasswordForm_InvalidConfirmPassword,
                required: this.i18n.Core_FieldRequired,
              })
              .build()
        )
        .build(),
    ];
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private builder: DfFormFieldBuilder,
    private activeRoute: ActivatedRoute,
    private accountService: AccountService,
    private webEnvironmentService: WebEnvironmentService
  ) {
    super();
  }

  public submit() {
    this.isSaving = true;
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.isSaving = false;
      return;
    }

    this.accountService
      .setResetPassword(
        this.code,
        this.form.value.email,
        this.form.value.newPassword,
        this.form.value.confirmPassword
      )
      .subscribe((response) => {
        const { hasTokenError, hasPasswordError, passwordChanged } = response;
        this.hasPasswordError = hasPasswordError;
        this.hasTokenError = hasTokenError;
        this.passwordChanged = passwordChanged;
        this.isSaving = false;
        this.cdr.detectChanges();
      });
  }
}
